import { useMutation } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import {
  CustomerDocumentInputSchema,
  type CustomerDocument,
  type CustomerDocumentInput,
  type CustomerDocumentKind,
} from "../../shared/customer-documents";
import { Button } from "../components/ui/button";
import { SheetBody, SheetFooter, SheetForm } from "../components/ui/sheet";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { CommercialFields } from "./CustomerDocumentFields";
import { CustomerDocumentSource } from "./CustomerDocumentSource";
import { saveCustomerLibraryDocument } from "./customer-documents-api";

function initialDraft(kind: CustomerDocumentKind, canUsePages: boolean): CustomerDocumentInput {
  return {
    kind,
    agreementType: kind === "agreement" ? "msa" : null,
    title: "",
    summary: null,
    status: kind === "knowledge" ? "current" : "draft",
    reference: null,
    amountMinor: null,
    documentDate: null,
    expiresOn: null,
    source:
      kind === "knowledge" && canUsePages
        ? { type: "page", pageId: "", revision: 1 }
        : { type: "file", fileId: "" },
  };
}
type DocumentFormProps = {
  slug: string;
  kind: CustomerDocumentKind;
  current?: CustomerDocument;
  canUsePages: boolean;
  onSaved: (document: CustomerDocument) => void;
  onCancel: () => void;
  onDirty: (dirty: boolean) => void;
  onBusy: (busy: boolean) => void;
  /** Replaces the Save draft footer, for a confirmation that needs the footer's space. */
  footer?: ReactNode;
};
export function CustomerDocumentForm({
  slug,
  kind,
  current,
  canUsePages,
  onSaved,
  onCancel,
  onDirty,
  onBusy,
  footer,
}: DocumentFormProps) {
  const [value, setDraft] = useState(
    () => current?.draft.fields ?? initialDraft(kind, canUsePages),
  );
  const setValue: typeof setDraft = (next) => {
    onDirty(true);
    setDraft(next);
  };
  const [uploading, setUploading] = useState(false);
  function uploadBusy(busy: boolean) {
    setUploading(busy);
    onBusy(busy);
  }
  const [validation, setValidation] = useState("");
  const save = useMutation({
    mutationFn: (draft: CustomerDocumentInput) => saveCustomerLibraryDocument(slug, draft, current),
    onSuccess: (result) => {
      onSaved(result.document);
    },
    onMutate: () => {
      onBusy(true);
    },
    onSettled: () => {
      onBusy(false);
    },
  });
  return (
    <SheetForm
      aria-label="Document details"
      onSubmit={(event) => {
        event.preventDefault();
        if (uploading || save.isPending) return;
        const parsed = prepareDraft(value);
        if (typeof parsed === "string") {
          setValidation(parsed);
          return;
        }
        setValidation("");
        save.mutate(parsed);
      }}
    >
      <SheetBody>
        <fieldset className="space-y-5" disabled={save.isPending}>
          <DocumentIdentity kind={kind} value={value} setValue={setValue} />
          {kind !== "knowledge" ? <CommercialFields value={value} onChange={setValue} /> : null}
        </fieldset>
        <fieldset disabled={uploading || save.isPending}>
          <CustomerDocumentSource
            slug={slug}
            value={value}
            onChange={setValue}
            canUsePages={canUsePages}
            onUploadBusy={uploadBusy}
          />
        </fieldset>
        {validation || save.error ? (
          <FieldError>{validation || save.error?.message}</FieldError>
        ) : null}
      </SheetBody>
      {footer ?? <DraftActions pending={save.isPending || uploading} onCancel={onCancel} />}
    </SheetForm>
  );
}

function DocumentIdentity({
  kind,
  value,
  setValue,
}: {
  kind: CustomerDocumentKind;
  value: CustomerDocumentInput;
  setValue: React.Dispatch<React.SetStateAction<CustomerDocumentInput>>;
}) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor="library-title">Title</FieldLabel>
        <Input
          autoFocus
          id="library-title"
          required
          maxLength={200}
          value={value.title}
          onChange={(event) => {
            setValue({ ...value, title: event.target.value });
          }}
          placeholder={
            kind === "agreement"
              ? "Master services agreement"
              : kind === "quote"
                ? "Quote title"
                : "Document title"
          }
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="library-summary">
          Summary <span className="font-normal text-muted-foreground">optional</span>
        </FieldLabel>
        <Textarea
          id="library-summary"
          rows={2}
          maxLength={2000}
          value={value.summary ?? ""}
          onChange={(event) => {
            setValue({ ...value, summary: event.target.value || null });
          }}
        />
        <p className="text-sm text-muted-foreground">
          This description will be visible with the published document.
        </p>
      </Field>
    </>
  );
}

function prepareDraft(value: CustomerDocumentInput) {
  const source = value.source;
  if (source.type === "file" && !source.fileId) return "Upload a PDF before saving.";
  if (source.type === "page" && !source.pageId) return "Choose an internal page before saving.";
  const parsed = CustomerDocumentInputSchema.safeParse(value);
  return parsed.success ? parsed.data : parsed.error.issues.map((issue) => issue.message).join(" ");
}

function DraftActions({ pending, onCancel }: { pending: boolean; onCancel: () => void }) {
  return (
    <SheetFooter
      start={
        <p className="m-0 text-sm text-muted-foreground">Saving keeps this version internal.</p>
      }
    >
      <Button type="button" variant="ghost" disabled={pending} onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={pending}>
        {pending ? "Saving…" : "Save draft"}
      </Button>
    </SheetFooter>
  );
}
